import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Truck, Clock, MapPin, Package } from 'lucide-react';

const ShippingPolicyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="shipping-policy-page">
      {/* Breadcrumb */}
      <div className="bg-[#FFF9F5] border-b">
        <div className="container-main py-4">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 font-medium">Shipping Policy</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="bg-gradient-to-br from-[#FFF9F5] to-[#FFE8D4] py-12">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Shipping Policy
            </h1>
            <p className="text-gray-600">Last updated: January 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            {/* Highlight Box */}
            <div className="bg-brand-green text-white p-6 rounded-3xl mb-8 flex items-center gap-4">
              <Truck className="w-10 h-10 flex-shrink-0" />
              <div>
                <h2 className="font-heading text-xl font-bold mb-1">
                  Free Shipping on Orders Over $50!
                </h2>
                <p className="opacity-90">
                  Enjoy free standard shipping on all orders over $50 within the United States.
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Shipping Options</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#FFF9F5]">
                        <th className="text-left p-4 font-heading font-bold text-gray-900">Shipping Method</th>
                        <th className="text-left p-4 font-heading font-bold text-gray-900">Delivery Time</th>
                        <th className="text-left p-4 font-heading font-bold text-gray-900">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-4 text-gray-700">Standard Shipping</td>
                        <td className="p-4 text-gray-600">5-7 Business Days</td>
                        <td className="p-4">
                          <span className="text-brand-green font-medium">FREE</span> on orders $50+<br />
                          <span className="text-gray-600">$5.99 for orders under $50</span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-4 text-gray-700">Express Shipping</td>
                        <td className="p-4 text-gray-600">2-3 Business Days</td>
                        <td className="p-4 text-gray-600">$12.99</td>
                      </tr>
                      <tr>
                        <td className="p-4 text-gray-700">Overnight Shipping</td>
                        <td className="p-4 text-gray-600">1 Business Day</td>
                        <td className="p-4 text-gray-600">$24.99</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Processing Time</h2>
                <div className="flex items-start gap-4 bg-[#FFF9F5] p-6 rounded-2xl">
                  <Clock className="w-8 h-8 text-brand-orange flex-shrink-0" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong>Orders are typically processed within 1-2 business days.</strong>
                    </p>
                    <p className="text-gray-600">
                      Orders placed after 2 PM EST or on weekends/holidays will be processed the next business day. During peak seasons, processing may take an additional 1-2 days.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Shipping Locations</h2>
                <div className="flex items-start gap-4 bg-[#FFF9F5] p-6 rounded-2xl">
                  <MapPin className="w-8 h-8 text-brand-orange flex-shrink-0" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong>We currently ship to all 50 U.S. states.</strong>
                    </p>
                    <p className="text-gray-600 mb-4">
                      We ship to residential and commercial addresses, including PO Boxes and APO/FPO addresses. Please note that delivery to PO Boxes and APO/FPO addresses may take additional time.
                    </p>
                    <p className="text-brand-orange font-medium">
                      International shipping is not available at this time. Stay tuned for updates!
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Order Tracking</h2>
                <div className="flex items-start gap-4 bg-[#FFF9F5] p-6 rounded-2xl">
                  <Package className="w-8 h-8 text-brand-orange flex-shrink-0" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      <strong>Track your order every step of the way.</strong>
                    </p>
                    <p className="text-gray-600">
                      Once your order ships, you'll receive an email with tracking information. You can track your package directly through the carrier's website or by contacting our customer support team.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Shipping Carriers</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We partner with reliable shipping carriers to ensure your order arrives safely:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>USPS (United States Postal Service)</li>
                  <li>UPS (United Parcel Service)</li>
                  <li>FedEx</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  The carrier used will depend on your location, order size, and selected shipping method.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Delivery Issues</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If you experience any issues with your delivery, please contact us:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li><strong>Missing Package:</strong> If tracking shows delivered but you haven't received your package, please wait 24 hours and check with neighbors. If still missing, contact us immediately.</li>
                  <li><strong>Damaged Package:</strong> If your package arrives damaged, take photos and contact us within 48 hours for a replacement.</li>
                  <li><strong>Wrong Address:</strong> Please ensure your shipping address is correct when placing your order. We are not responsible for orders shipped to incorrect addresses provided by the customer.</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Holiday Shipping</h2>
                <p className="text-gray-600 leading-relaxed">
                  During peak holiday seasons (Thanksgiving, Christmas, etc.), shipping carriers may experience delays. We recommend placing orders early to ensure timely delivery. We will communicate any expected delays through our website and email updates.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4">Questions?</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you have any questions about shipping, please contact our customer support team:
                </p>
                <div className="mt-4 bg-[#FFF9F5] p-6 rounded-2xl">
                  <p className="text-gray-700"><strong>NexaFusion Shipping Support</strong></p>
                  <p className="text-gray-600">Email: support@nexafusion.store</p>
                  <p className="text-gray-600">Phone: +1-888-728-1766</p>
                  <p className="text-gray-600">Hours: Monday - Friday, 9am - 6pm EST</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShippingPolicyPage;
